// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import CuzprLogo from '../assets/CuzprLogo.png';

const Navbar = ({ currentThread }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);

      // Listen for authentication changes
      const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
        setUser(session?.user || null);
      });

      return () => listener.subscription.unsubscribe();
    };

    fetchUser();
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error during logout:', error.message);
    } else {
      navigate('/'); // Redirect to the login page after logout
    }
  };

  return (
    <nav className="bg-gray-800 shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 py-2 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={CuzprLogo} alt="Cuzpr Logo" className="h-12 w-auto" />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-6 items-center">
          {user ? (
            // When logged in: show Dashboard, Assistant, Modules, Calendar, Account Settings, and Logout.
            <>
              <Link to="/dashboard" className="text-white hover:text-blue-300">
                Dashboard
              </Link>
              <Link to="/assistant" className="text-white hover:text-blue-300">
                Assistant
              </Link>
              <Link to="/modules" className="text-white hover:text-blue-300">
                Modules
              </Link>
              <Link to="/calendar" className="text-white hover:text-blue-300">
                Calendar
              </Link>
              <Link to="/settings" className="text-white hover:text-blue-300">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.121 17.804A10.966 10.966 0 0112 15c2.756 0 5.262.998 7.121 2.804M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </Link>
              <button
                onClick={handleLogout}
                className="text-white hover:text-red-300"
              >
                Logout
              </button>
            </>
          ) : (
            // When logged out: show Product, Case Studies, Pricing, and Signup.
            <>
              <Link to="/product" className="text-white hover:text-blue-300">
                Product
              </Link>
              <Link to="/case-studies" className="text-white hover:text-blue-300">
                Case Studies
              </Link>
              <Link to="/pricing" className="text-white hover:text-blue-300">
                Pricing
              </Link>
              <Link 
                to="/signup" 
                className="text-white bg-green-500 hover:bg-green-600 px-3 py-1 rounded"
              >
                Signup
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="block md:hidden text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-gray-800 shadow-md">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link
                to="/assistant"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Assistant
              </Link>
              <Link
                to="/modules"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Modules
              </Link>
              <Link
                to="/calendar"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Calendar
              </Link>
              <Link
                to="/settings"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Account Settings
              </Link>
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700 hover:text-red-300"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/product"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Product
              </Link>
              <Link
                to="/case-studies"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Case Studies
              </Link>
              <Link
                to="/pricing"
                className="block px-4 py-2 text-white hover:bg-gray-700 hover:text-blue-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link 
                to="/signup" 
                className="block px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded"
                onClick={() => setIsMenuOpen(false)}
              >
                Signup
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
